import React, { useState, useEffect } from "react";
import { api } from "../../services/api";
import { Icon } from "@iconify/react/dist/iconify.js";

const StudentDetailedStats = () => {
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [studentData, setStudentData] = useState({
    progress: {},
    examResults: [],
    quizResults: [],
    loading: true,
  });
  const [loading, setLoading] = useState(true);

  // Fetch the list of students on component mount
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const response = await api.users.getAll();
        if (response.success) {
          // Filter to users with role USER or STUDENT and get the first 6
          const filteredStudents = response.data
            .filter((user) => user.role === "USER" || user.role === "STUDENT")
            .slice(0, 6);

          setStudents(filteredStudents);

          // Auto-select the first student if available
          if (filteredStudents.length > 0) {
            setSelectedStudentId(filteredStudents[0].id);
          }
        } else {
          console.error("Failed to fetch students:", response.message);
        }
      } catch (error) {
        console.error("Error fetching students:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, []);

  // Fetch detailed data when a student is selected
  useEffect(() => {
    const fetchStudentDetails = async () => {
      if (!selectedStudentId) return;

      setStudentData((prev) => ({ ...prev, loading: true }));

      try {
        // Fetch user progress
        const progressResponse = await api.userProgress.getByUserId(
          selectedStudentId
        );

        // Fetch exam results
        const examResultsResponse = await api.exams.getUserResults(
          selectedStudentId
        );

        // Fetch quiz results
        const quizResultsResponse = await api.quizzes.getUserResults(
          selectedStudentId
        );

        setStudentData({
          progress: progressResponse.success ? progressResponse.data : {},
          examResults: examResultsResponse.success
            ? examResultsResponse.data.slice(0, 3)
            : [], // Get most recent 3 exams
          quizResults: quizResultsResponse.success
            ? quizResultsResponse.data.slice(0, 3)
            : [], // Get most recent 3 quizzes
          loading: false,
        });
      } catch (error) {
        console.error(
          `Error fetching details for student ${selectedStudentId}:`,
          error
        );
        setStudentData({
          progress: {},
          examResults: [],
          quizResults: [],
          loading: false,
        });
      }
    };

    fetchStudentDetails();
  }, [selectedStudentId]);

  // Get the selected student object
  const selectedStudent = students.find((s) => s.id === selectedStudentId);

  // Format date to a user-friendly string
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Helper functions to properly display names and data
  // Get exam name from result
  const getExamName = (result) => {
    // Check different possible structures based on Prisma schema
    if (result.exam && result.exam.title) {
      return result.exam.title;
    }
    return result.examName || result.title || "Unknown Exam";
  };

  // Get quiz name from result
  const getQuizName = (result) => {
    // Check different possible structures based on Prisma schema
    if (result.quiz && result.quiz.title) {
      return result.quiz.title;
    }
    return result.quizName || result.title || "Unknown Quiz";
  };

  // Get exam score percentage
  const getExamScore = (result) => {
    return result.score || result.scorePercentage || 0;
  };

  // Check if the exam was passed
  const wasExamPassed = (result) => {
    return result.passed || result.isPassed || false;
  };

  // Default placeholder image if user doesn't have one
  const defaultUserImage = "assets/images/home-six/student-img1.png";

  // CSS styles for circular progress
  const circularProgressStyles = {
    container: {
      position: "relative",
      width: "140px",
      height: "140px",
      margin: "0 auto",
      borderRadius: "50%",
      background: "#f5f5f5",
    },
    progressCircle: (progress) => ({
      position: "absolute",
      top: "0",
      left: "0",
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      background: `conic-gradient(var(--bs-primary) ${
        progress * 3.6
      }deg, transparent 0deg)`,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }),
    innerCircle: {
      width: "80%",
      height: "80%",
      borderRadius: "50%",
      background: "white",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      boxShadow: "0 0 10px rgba(0,0,0,0.05)",
    },
    progressValue: {
      fontSize: "24px",
      fontWeight: "bold",
      color: "var(--bs-primary)",
    },
    progressLabel: {
      fontSize: "14px",
      color: "#6c757d",
    },
  };

  return (
    <div className="col-xxl-8 col-md-12">
      <div className="card shadow-sm">
        <div className="card-header bg-white py-3">
          <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between">
            <h6 className="mb-0 fw-bold fs-5">Student Detailed Statistics</h6>
          </div>
        </div>

        {loading ? (
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading students...</span>
            </div>
            <p className="mt-2">Loading students...</p>
          </div>
        ) : students.length === 0 ? (
          <div className="card-body text-center py-5">
            <Icon
              icon="solar:user-cross-linear"
              className="fs-1 text-secondary mb-2"
            />
            <p className="mb-0">No students found</p>
          </div>
        ) : (
          <>
            {/* Student selector */}
            <div className="card-body pb-0 border-bottom">
              <div className="d-flex flex-wrap gap-3 mb-2 justify-content-center justify-content-md-start">
                {students.map((student) => (
                  <div
                    key={student.id}
                    className={`cursor-pointer rounded-3 p-3 d-flex align-items-center gap-2 transition-all ${
                      selectedStudentId === student.id
                        ? "bg-primary-subtle shadow-sm"
                        : "bg-light hover-bg-light-300"
                    }`}
                    onClick={() => setSelectedStudentId(student.id)}
                    style={{ minWidth: "150px" }}
                  >
                    <div className="position-relative">
                      <div
                        className={`rounded-circle overflow-hidden border-2 ${
                          selectedStudentId === student.id
                            ? "border border-primary"
                            : ""
                        }`}
                        style={{ width: "40px", height: "40px" }}
                      >
                        <img
                          src={student.avatar || defaultUserImage}
                          alt={`${student.firstName} ${student.lastName}`}
                          className="w-100 h-100 object-fit-cover"
                        />
                      </div>
                      {selectedStudentId === student.id && (
                        <div
                          className="position-absolute bottom-0 end-0 bg-primary rounded-circle"
                          style={{ width: "10px", height: "10px" }}
                        ></div>
                      )}
                    </div>
                    <span
                      className={`${
                        selectedStudentId === student.id
                          ? "text-primary fw-bold"
                          : ""
                      }`}
                    >
                      {student.firstName} {student.lastName}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Student details */}
            <div className="card-body py-4">
              {!selectedStudentId ? (
                <div className="text-center py-5">
                  <Icon
                    icon="solar:user-broken"
                    className="fs-1 text-secondary mb-2"
                  />
                  <p className="mb-0">Please select a student</p>
                </div>
              ) : studentData.loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">
                      Loading student data...
                    </span>
                  </div>
                  <p className="mt-2">Loading student data...</p>
                </div>
              ) : (
                <div className="row g-4">
                  {/* Left column - Summary stats */}
                  <div className="col-md-4">
                    <div className="d-flex flex-column h-100 justify-content-between bg-light rounded-4 p-4">
                      <div>
                        <div className="text-center mb-4">
                          <div
                            className="rounded-circle overflow-hidden border border-2 border-primary mx-auto mb-3"
                            style={{ width: "80px", height: "80px" }}
                          >
                            <img
                              src={selectedStudent?.avatar || defaultUserImage}
                              alt={`${selectedStudent?.firstName} ${selectedStudent?.lastName}`}
                              className="w-100 h-100 object-fit-cover"
                            />
                          </div>
                          <h5 className="mb-1 fw-bold">
                            {selectedStudent?.firstName}{" "}
                            {selectedStudent?.lastName}
                          </h5>
                          <p className="text-secondary mb-0 small">
                            {selectedStudent?.email}
                          </p>
                        </div>

                        <div className="mb-4">
                          <div style={circularProgressStyles.container}>
                            <div
                              style={circularProgressStyles.progressCircle(
                                studentData.progress?.overallProgress || 0
                              )}
                            >
                              <div style={circularProgressStyles.innerCircle}>
                                <span
                                  style={circularProgressStyles.progressValue}
                                >
                                  {studentData.progress?.overallProgress || 0}%
                                </span>
                                <span
                                  style={circularProgressStyles.progressLabel}
                                >
                                  Progress
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="d-flex flex-column gap-3 mt-4 bg-white p-3 rounded-3 shadow-sm">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-secondary">
                            <Icon
                              icon="solar:notebook-linear"
                              className="me-2"
                            />
                            Exams Taken:
                          </span>
                          <span className="fw-bold badge bg-light-subtle text-dark">
                            {studentData.progress?.totalExamsTaken || 0}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-secondary">
                            <Icon
                              icon="solar:diploma-verified-linear"
                              className="me-2"
                            />
                            Exams Passed:
                          </span>
                          <span className="fw-bold badge bg-success-subtle text-success">
                            {studentData.progress?.totalExamsPassed || 0}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-secondary">
                            <Icon
                              icon="solar:clipboard-list-linear"
                              className="me-2"
                            />
                            Quizzes Taken:
                          </span>
                          <span className="fw-bold badge bg-light-subtle text-dark">
                            {studentData.progress?.totalQuizzesTaken || 0}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-secondary">
                            <Icon
                              icon="solar:chat-round-dots-linear"
                              className="me-2"
                            />
                            Questions:
                          </span>
                          <span className="fw-bold badge bg-light-subtle text-dark">
                            {studentData.progress?.totalPracticeQuestions || 0}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-secondary">
                            <Icon icon="solar:chart-linear" className="me-2" />
                            Average Score:
                          </span>
                          <span className="fw-bold badge bg-primary-subtle text-primary">
                            {studentData.progress?.averageScore || 0}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right column - Recent results */}
                  <div className="col-md-8">
                    {/* Recent exam results */}
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0 fw-bold">
                          <Icon
                            icon="solar:test-outline"
                            className="me-2 text-primary"
                          />
                          Recent Exam Results
                        </h6>
                        {studentData.examResults?.length > 0 && (
                          <a
                            href={`/exams/student/${selectedStudentId}`}
                            className="text-primary text-decoration-none small"
                          >
                            View All{" "}
                            <Icon
                              icon="solar:alt-arrow-right-linear"
                              className="icon"
                            />
                          </a>
                        )}
                      </div>

                      {studentData.examResults?.length === 0 ? (
                        <div className="text-center py-4 bg-light rounded-3 shadow-sm">
                          <Icon
                            icon="solar:document-text-broken"
                            className="fs-1 text-secondary mb-2"
                          />
                          <p className="mb-0 text-secondary">
                            No exam results found
                          </p>
                        </div>
                      ) : (
                        <div className="table-responsive shadow-sm rounded-3 overflow-hidden">
                          <table className="table table-hover mb-0">
                            <thead className="bg-light">
                              <tr>
                                <th className="ps-3">Exam</th>
                                <th>Date</th>
                                <th>Score</th>
                                <th className="pe-3">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {studentData.examResults.map((result) => (
                                <tr key={result.id}>
                                  <td className="ps-3 py-3">
                                    {getExamName(result)}
                                  </td>
                                  <td className="py-3">
                                    {formatDate(
                                      result.completedAt || result.createdAt
                                    )}
                                  </td>
                                  <td className="py-3">
                                    {getExamScore(result)}%
                                  </td>
                                  <td className="pe-3 py-3">
                                    <span
                                      className={`badge ${
                                        wasExamPassed(result)
                                          ? "bg-success"
                                          : "bg-danger"
                                      }`}
                                    >
                                      {wasExamPassed(result)
                                        ? "Passed"
                                        : "Failed"}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Recent quiz results */}
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0 fw-bold">
                          <Icon
                            icon="solar:quiz-linear"
                            className="me-2 text-primary"
                          />
                          Recent Quiz Results
                        </h6>
                        {studentData.quizResults?.length > 0 && (
                          <a
                            href={`/quizzes/student/${selectedStudentId}`}
                            className="text-primary text-decoration-none small"
                          >
                            View All{" "}
                            <Icon
                              icon="solar:alt-arrow-right-linear"
                              className="icon"
                            />
                          </a>
                        )}
                      </div>

                      {studentData.quizResults?.length === 0 ? (
                        <div className="text-center py-4 bg-light rounded-3 shadow-sm">
                          <Icon
                            icon="solar:documents-minimalistic-broken"
                            className="fs-1 text-secondary mb-2"
                          />
                          <p className="mb-0 text-secondary">
                            No quiz results found
                          </p>
                        </div>
                      ) : (
                        <div className="table-responsive shadow-sm rounded-3 overflow-hidden">
                          <table className="table table-hover mb-0">
                            <thead className="bg-light">
                              <tr>
                                <th className="ps-3">Quiz</th>
                                <th>Date</th>
                                <th>Score</th>
                                <th className="pe-3">Time Taken</th>
                              </tr>
                            </thead>
                            <tbody>
                              {studentData.quizResults.map((result) => (
                                <tr key={result.id}>
                                  <td className="ps-3 py-3">
                                    {getQuizName(result)}
                                  </td>
                                  <td className="py-3">
                                    {formatDate(
                                      result.completedAt || result.createdAt
                                    )}
                                  </td>
                                  <td className="py-3">{result.score || 0}%</td>
                                  <td className="pe-3 py-3">
                                    <span className="badge bg-info-subtle text-info">
                                      {Math.floor((result.timeSpent || 0) / 60)}{" "}
                                      min
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StudentDetailedStats;
